import { Spin } from 'antd';
import classNames from 'classnames';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { SELECTION_CIRCLES } from '@common/constants';
import { CustomSideType, containsBumpOut, containsRadiusOut } from '@common/enums';
import { DrawingShape } from '@common/models';
import { CustomSideModel } from '@common/models/predefined-custom-side.model';
import { SectionSubtitle } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { selectSelectedCustomSide, updateSelectedCustomSide } from '@redux/slices/customSideSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import { updateSelectionCircles } from '@redux/slices/fabricSlice';

import useCustomSides from './useCustomSides';

interface CustomSideItemProps {
    customSide: CustomSideModel;
}

const CustomSide = (): JSX.Element => {
    const { t } = useTranslation();

    const selectedCustomSide = useAppSelector(selectSelectedCustomSide);

    const dispatch = useAppDispatch();

    const { areCustomSidesLoading, customSides } = useCustomSides();
    const { drawingShapes } = useContext(DrawingShapeContext);

    const { trackSelectCustomSide } = useAnalyticsEvents();

    const selectCustomSide = (customSide: CustomSideModel, wasPreviouslySelected: boolean) => {
        if (
            wasPreviouslySelected ||
            (customSide.type === CustomSideType.REMOVE_CUSTOM_SIDE && !isCustomSideRemoveEnabled())
        ) {
            return;
        }

        trackSelectCustomSide({ side: customSide });

        dispatch(updateSelectedCustomSide(customSide));
        dispatch(updateSelectionCircles(SELECTION_CIRCLES.FOR_CUSTOM_SIDES));
        dispatch(resetSelectedDiagonalCorner());
        dispatch(resetSelectedCornerRadius());
    };

    const isCustomSideRemoveEnabled = (): boolean =>
        drawingShapes.some((shape: DrawingShape) =>
            shape.drawingEdges.some((edge) => containsBumpOut(edge) || containsRadiusOut(edge))
        );

    const isCardOptionDisabled = (customSide: CustomSideModel): boolean =>
        customSide.type === CustomSideType.REMOVE_CUSTOM_SIDE && !isCustomSideRemoveEnabled();

    const CustomSideItem = ({ customSide }: CustomSideItemProps) => {
        const isSelected =
            customSide.id === selectedCustomSide?.id &&
            !(customSide.type === CustomSideType.REMOVE_CUSTOM_SIDE && !isCustomSideRemoveEnabled());

        return (
            <div
                className={classNames('card-option single-action-card', {
                    selected: isSelected,
                    disabled: isCardOptionDisabled(customSide),
                })}
                onClick={() => selectCustomSide(customSide, isSelected)}
            >
                <img alt="custom-side" className="card-option__image" src={customSide.image} />
                <div className="name">{customSide.title}</div>
                <div className="price">&emsp;</div>
            </div>
        );
    };
    const CustomSideContent = () =>
        areCustomSidesLoading ? (
            <Spin style={{ fontSize: 24 }} />
        ) : (
            <div className="card-single-select">
                {customSides?.map((customSide, id) => (
                    <CustomSideItem key={id} customSide={customSide} />
                ))}
            </div>
        );

    return customSides?.length ? (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.CUSTOM_SIDE.TITLE')}
                description={t('SCROLLABLE_AREA.CUSTOM_SIDE.DESCRIPTION')}
                showLine
            />
            <CustomSideContent />
        </>
    ) : (
        <></>
    );
};

export default CustomSide;
